import { TIER_ORDER, type QualityTier } from './quality'

/**
 * Decides when the renderer should drop a quality tier.
 *
 * Pure: frame times in, an occasional tier out. No Three.js, no WebGL, no DOM.
 * That is not tidiness for its own sake -- it is the only way this policy can
 * be tested at all. Headless Chrome on this machine renders the scene at over
 * 700fps under a software rasteriser, so a browser harness can never make the
 * frame budget fail on purpose, and an adaptive rule that has never once been
 * observed to fire is indistinguishable from one that cannot. Driven directly
 * with synthetic frame times, every branch is reachable in milliseconds.
 *
 * The same reasoning is why terrain, physics, race and scoring import nothing
 * from the renderer.
 */
export class QualityGovernor {
  private elapsed = 0
  private frames = 0
  private slow = 0
  private steps = 0

  constructor(
    private tier: QualityTier,
    /** An explicit ?quality= is a decision, not a suggestion. */
    private readonly locked: boolean,
  ) {}

  /**
   * Feed one frame. Returns the tier to switch to, or null to stay put.
   *
   * Only ever downward, and at most twice. A rule that can also step back up
   * will find a boundary and oscillate across it, and a resolution change every
   * few seconds is far more distracting than the frame it was trying to save.
   */
  frame(frameDt: number): QualityTier | null {
    if (this.locked || this.steps >= MAX_STEPS) return null

    // Slope build, shader compile and the first shadow pass all land in the
    // first seconds and none of them repeat. Judging them would step a device
    // down for work it will never do again.
    this.elapsed += frameDt
    if (this.elapsed < WARMUP_SECONDS) return null

    this.frames++
    if (frameDt > FRAME_BUDGET) this.slow++
    if (this.frames < WINDOW_FRAMES) return null

    const slowShare = this.slow / this.frames
    this.frames = 0
    this.slow = 0
    // Strictly more than half, not at least half. Stepping down is visible
    // and never reverses, so a borderline window should be left alone.
    if (slowShare <= SLOW_SHARE) return null

    const i = TIER_ORDER.indexOf(this.tier)
    if (i <= 0) {
      // Already at the floor. Stop counting rather than re-deciding forever.
      this.steps = MAX_STEPS
      return null
    }
    this.tier = TIER_ORDER[i - 1]!
    this.steps++
    return this.tier
  }

  /** Share of the last completed window that missed budget, for logging. */
  get lastSlowShare(): number {
    return this.frames > 0 ? this.slow / this.frames : 0
  }
}

/**
 * 34ms — a 30fps floor, not a 60fps target. The game is quality-first by
 * request ('screw the 60 fps'): the tiers spend the phone on looking real,
 * and adaptation exists only to rescue a device that cannot even hold a
 * playable frame rate, not to protect a smooth one.
 */
export const FRAME_BUDGET = 0.034
/** Judged over a window of frames, not seconds -- the unit cost is a frame. */
export const WINDOW_FRAMES = 150
/** A window must be *more* than this share slow to count against the device. */
export const SLOW_SHARE = 0.5
export const WARMUP_SECONDS = 4
export const MAX_STEPS = 2
